import { useCallback, useEffect, useState } from 'react';
import { AUTH_PATHS } from './auth';

/**
 * Adresy ekranów panelu — po polsku, jak w aplikacji: adres jest identyfikatorem, nie treścią.
 * Router własny, bo ekranów jest kilka, a „wstecz" ma cofać o ekran, nie wyrzucać z panelu.
 */
export type Route =
  | { screen: 'start' }
  | { screen: 'credits' }
  | { screen: 'pricing' }
  | { screen: 'cycle' }
  | { screen: 'providers' }
  | { screen: 'companies' }
  | { screen: 'company'; companyId: string }
  | { screen: 'offices' }
  | { screen: 'office'; officeId: string }
  | { screen: 'templates' }
  | { screen: 'template'; templateId: string }
  | { screen: 'schemes' }
  | { screen: 'schemeNew' }
  | { screen: 'taxOffices' }
  | { screen: 'inboundMail' }
  | { screen: 'scheme'; schemeId: string };

export type Screen = Route['screen'];

export function isAuthPath(pathname: string): boolean {
  return Object.values(AUTH_PATHS).includes(pathname);
}

export function parsePath(pathname: string): Route {
  const [first, second] = pathname.split('/').filter(Boolean).map(decodeURIComponent);
  if (first === 'kredyty') return { screen: 'credits' };
  if (first === 'cenniki') return { screen: 'pricing' };
  if (first === 'obciazenia') return { screen: 'cycle' };
  if (first === 'operatorzy-platnosci') return { screen: 'providers' };
  if (first === 'firmy') return second ? { screen: 'company', companyId: second } : { screen: 'companies' };
  if (first === 'biura') return second ? { screen: 'office', officeId: second } : { screen: 'offices' };
  if (first === 'urzedy') return { screen: 'taxOffices' };
  if (first === 'poczta') return { screen: 'inboundMail' };
  if (first === 'wzorce') return second ? { screen: 'template', templateId: second } : { screen: 'templates' };
  if (first === 'schematy') {
    if (second === 'nowy') return { screen: 'schemeNew' };
    return second ? { screen: 'scheme', schemeId: second } : { screen: 'schemes' };
  }
  return { screen: 'start' };
}

export function pathOf(route: Route): string {
  switch (route.screen) {
    case 'start':
      return '/';
    case 'credits':
      return '/kredyty';
    case 'pricing':
      return '/cenniki';
    case 'cycle':
      return '/obciazenia';
    case 'providers':
      return '/operatorzy-platnosci';
    case 'companies':
      return '/firmy';
    case 'company':
      return `/firmy/${route.companyId}`;
    case 'offices':
      return '/biura';
    case 'office':
      return `/biura/${route.officeId}`;
    case 'templates':
      return '/wzorce';
    case 'template':
      return `/wzorce/${route.templateId}`;
    case 'schemes':
      return '/schematy';
    case 'schemeNew':
      return '/schematy/nowy';
    case 'taxOffices':
      return '/urzedy';
    case 'inboundMail':
      return '/poczta';
    case 'scheme':
      return `/schematy/${route.schemeId}`;
  }
}

export type Navigate = (next: Route, options?: { replace?: boolean }) => void;

export function useRoute(): [Route, Navigate] {
  const [route, setRoute] = useState<Route>(() => parsePath(window.location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(parsePath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Adres doprowadzony do postaci kanonicznej — poza adresami logowania, które niosą kod z Keycloaka
  useEffect(() => {
    if (isAuthPath(window.location.pathname)) return;
    const canonical = pathOf(parsePath(window.location.pathname));
    if (canonical !== window.location.pathname) window.history.replaceState({}, '', canonical);
  }, []);

  const navigate = useCallback<Navigate>((next, options) => {
    const path = pathOf(next);
    if (options?.replace || path === window.location.pathname) {
      window.history.replaceState({}, '', path);
    } else {
      window.history.pushState({}, '', path);
    }
    setRoute(next);
  }, []);

  return [route, navigate];
}
